/**
 * OpenRouter Token Usage Analytics (Vite module)
 *
 * Every figure here is measured. Weekly token volumes come from OpenRouter's
 * public rankings API, per-model detail from its per-model endpoint, and
 * release dates / context windows / prices from the model catalog. All three
 * are baked into src/data/catalog.json by scripts/update-data.mjs, which
 * "npm run deploy" runs before every build.
 */
import CATALOG from './data/catalog.json';
import {
  select,
  scaleUtc,
  scaleBand,
  scaleLinear,
  scaleLog,
  axisBottom,
  axisLeft,
  line as d3Line,
  area as d3Area,
  stack as d3Stack,
  stackOrderNone,
  stackOffsetNone,
  stackOffsetExpand,
  max as d3Max,
  min as d3Min,
  bisector,
  utcFormat,
  format as d3Format,
  pointer
} from 'd3';
import './styles.css';

// Global state
const state = {
  currentView: 'total', // 'total' | 'stacked' | 'families' | 'market-share' | 'monthly' | 'catalog'
  selectedScope: 'all', // 'all', 'group:...' or specific model slug
  showMA: true,
  showEvents: true, // Show release milestone points along the X-axis
  yScale: 'linear',
  unit: 'T', // 'T', 'B', 'M', 'raw'
  activeWeeks: 52,
  isLive: false,
  rawDataset: []
};

// ---------------------------------------------------------------------------
// Roster, families, milestones and the weekly volume series all come from
// src/data/catalog.json, regenerated by `npm run update-data` (which
// `npm run deploy` runs first). One caveat worth knowing, carried in
// CATALOG.usageNote and rendered verbatim in the footer: the weekly series
// publishes the top nine models per week plus an aggregated "Others" bucket.
// ---------------------------------------------------------------------------
const AGGREGATED_GROUPS = {};
CATALOG.families.forEach(f => {
  AGGREGATED_GROUPS[f.id] = { ...f };
});

const FRONTIER_RELEASE_MILESTONES = CATALOG.milestones.map(m => ({ ...m }));

const TRACKED_MODELS = CATALOG.models.map(m => ({ ...m }));

const MODEL_CONFIG = {};
TRACKED_MODELS.forEach(m => {
  MODEL_CONFIG[m.slug] = m;
});

// ---------------------------------------------------------------------------
// Terminal chrome: flat black / grey with an amber phosphor accent.
// ---------------------------------------------------------------------------
const TERM = {
  accent: '#ffb000',
  accentDim: '#c98a00',
  accentDeep: '#6b4a00',
  bgPanel: '#0a0a0a',
  bgInset: '#050505',
  grid: '#1a1a1a',
  axis: '#333333',
  textPrimary: '#e4e4e4',
  textSecondary: '#8c8c8c',
  textMuted: '#5a5a5a',
  font: 'JetBrains Mono, monospace'
};

// ---------------------------------------------------------------------------
// Model families each get their own hue; individual models within a family are
// shaded from newest (brightest) to oldest, so a stacked chart reads as
// provider bands rather than 50 unrelated colours.
// ---------------------------------------------------------------------------
const PROVIDER_PALETTE = {
  OpenAI:    ['#00e5a0', '#00b881', '#008c62', '#006147'],
  Anthropic: ['#ff8a3d', '#e06b22', '#b34f13', '#80360a'],
  Google:    ['#4d9dff', '#2f7ad9', '#1f5aa8', '#143d73'],
  xAI:       ['#c77dff', '#a05ad9', '#7a3fa8', '#552b73'],
  DeepSeek:  ['#00d4ff', '#00a8cc', '#007d99', '#005266'],
  Meta:      ['#ff6b9d', '#d94a79', '#a83358', '#73203b'],
  Mistral:   ['#ffb000', '#d99000', '#a86e00', '#734b00'],
  Alibaba:   ['#a3e635', '#7cb518', '#5a840f', '#3d5a09'],
  Cohere:    ['#2dd4bf', '#14a89a', '#0d7a70', '#075049'],
  Microsoft: ['#8b95ff', '#6670d9', '#4a52a8', '#333873'],
  Nous:      ['#e879a6', '#c2557f', '#8f3b5c'],
  NVIDIA:    ['#76b900', '#5a8c00', '#3f6300'],
  Liquid:    ['#d4b483', '#a88c5f'],
  'Z.ai':    ['#f0abfc', '#c56ed0', '#8f4a99'],
  Moonshot:  ['#7dd3fc', '#38a8e0', '#1f7bab'],
  ByteDance: ['#fca5a5', '#e06666', '#a83f3f'],
  MiniMax:   ['#fcd34d', '#d9a520', '#a87b12'],
  Amazon:    ['#ffa726', '#cc7f14', '#8f570a'],
  Aggregated: ['#3a3a3a'],
  Unknown:   ['#6e6e6e', '#585858', '#454545']
};

function providerColor(provider, seenCounts) {
  const ramp = PROVIDER_PALETTE[provider] || PROVIDER_PALETTE.Unknown;
  const n = seenCounts[provider] || 0;
  seenCounts[provider] = n + 1;
  return ramp[n % ramp.length];
}

// Family aggregations take the flagship hue of their provider
Object.values(AGGREGATED_GROUPS).forEach(grp => {
  const ramp = PROVIDER_PALETTE[grp.provider] || PROVIDER_PALETTE.Unknown;
  grp.color = ramp[0];
});

const providerSeen = {};
TRACKED_MODELS.forEach(m => {
  m.color = m.slug === 'other' ? '#3a3a3a' : providerColor(m.provider, providerSeen);
});

// The daily feed reaches models the weekly top-9 never surfaces, so it carries
// its own roster. Colour it from the same provider ramps for consistency.
const DAILY_MODEL_CONFIG = {};
if (CATALOG.daily) {
  const dailySeen = {};
  CATALOG.daily.models
    .slice()
    .sort((a, b) => a.provider.localeCompare(b.provider) || a.slug.localeCompare(b.slug))
    .forEach(m => {
      DAILY_MODEL_CONFIG[m.slug] = {
        ...m,
        color: m.slug === 'Others' ? '#3a3a3a' : providerColor(m.provider, dailySeen)
      };
    });
}

// Milestones take their provider's hue — the same colour the table and the
// family bands use — so a marker reads as "an Anthropic release" at a glance.
// Many marked releases never cracked the weekly top nine, so they have no
// entry in MODEL_CONFIG to inherit from; the provider ramp always resolves.
FRONTIER_RELEASE_MILESTONES.forEach(m => {
  const ramp = PROVIDER_PALETTE[m.provider] || PROVIDER_PALETTE.Unknown;
  m.color = ramp[0];
});

// Formatters
function formatTokens(value, unit = state.unit) {
  if (value === undefined || value === null || isNaN(value)) return '0';
  if (unit === 'T') return (value / 1e12).toFixed(2) + ' T';
  if (unit === 'B') return (value / 1e9).toFixed(2) + ' B';
  if (unit === 'M') return (value / 1e6).toFixed(1) + ' M';
  return Number(value).toLocaleString();
}

function getUnitMultiplier(unit = state.unit) {
  if (unit === 'T') return 1e12;
  if (unit === 'B') return 1e9;
  if (unit === 'M') return 1e6;
  return 1;
}

function getUnitSuffix(unit = state.unit) {
  if (unit === 'T') return 'Trillion Tokens';
  if (unit === 'B') return 'Billion Tokens';
  if (unit === 'M') return 'Million Tokens';
  return 'Tokens';
}

/**
 * The measured weekly series, straight from src/data/catalog.json.
 *
 * Each point is one ISO week as OpenRouter publishes it: the top nine models
 * by volume plus an aggregated "Others" bucket. The week currently in progress
 * is dropped — it is a partial count and would read as a cliff on the chart.
 */
function loadMeasuredDataset() {
  return CATALOG.weeks
    .filter(w => !w.partial)
    .map(w => ({
      date: w.week,
      timestamp: Date.parse(w.week + 'T00:00:00Z'),
      totalTokens: w.total,
      models: { ...w.models }
    }));
}

/**
 * The credentialed daily series, present only when the build had an API key.
 * Trailing 30 days, top 50 models per day plus "Others" — far better coverage
 * than the weekly feed's top 9, but a much shorter history, so it lives
 * alongside the weekly series rather than replacing it.
 */
function loadDailyDataset() {
  if (!CATALOG.daily || !CATALOG.daily.days.length) return [];
  return CATALOG.daily.days.map(d => ({
    date: d.date,
    timestamp: Date.parse(d.date + 'T00:00:00Z'),
    totalTokens: d.total,
    models: { ...d.models }
  }));
}

const DAILY_AVAILABLE = Boolean(CATALOG.daily && CATALOG.daily.days.length);

/** Which measured series the current view reads from. */
function activeDataset() {
  return state.currentView === 'daily' ? loadDailyDataset() : state.rawDataset;
}

/** "day" or "week", for labels that would otherwise hardcode one. */
function granularity() {
  return state.currentView === 'daily' ? 'day' : 'week';
}

/**
 * Calculates 7-day rolling moving average
 */
function calculateMovingAverage(data, windowSize = 7) {
  const result = [];
  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - windowSize + 1);
    const subset = data.slice(start, i + 1);
    const sum = subset.reduce((acc, val) => acc + val, 0);
    result.push(sum / subset.length);
  }
  return result;
}

/**
 * Populate Model Dropdown with Aggregated Families + tracked models
 */
function populateModelDropdown() {
  const dropdown = document.getElementById('model-filter-select');
  if (!dropdown) return;

  const currentVal = dropdown.value || 'all';
  dropdown.innerHTML = '';

  const allOpt = document.createElement('option');
  allOpt.value = 'all';
  allOpt.textContent = '⚡ All Models (Platform Total)';
  dropdown.appendChild(allOpt);

  const groupHeader = document.createElement('optgroup');
  groupHeader.label = '── 🚀 Aggregated Frontier Families ──';

  Object.values(AGGREGATED_GROUPS).forEach(grp => {
    const opt = document.createElement('option');
    opt.value = grp.id;
    opt.textContent = `★ ${grp.label}`;
    groupHeader.appendChild(opt);
  });
  dropdown.appendChild(groupHeader);

  const modelsHeader = document.createElement('optgroup');
  modelsHeader.label = '── 🔬 Individual Tracked Models ──';

  TRACKED_MODELS.forEach((model, index) => {
    if (model.slug === 'other') return;
    const opt = document.createElement('option');
    opt.value = model.slug;
    opt.textContent = `#${index + 1} ${model.label} (${model.provider})`;
    modelsHeader.appendChild(opt);
  });

  const otherOpt = document.createElement('option');
  otherOpt.value = 'other';
  otherOpt.textContent = 'Other Models (Long Tail)';
  modelsHeader.appendChild(otherOpt);

  dropdown.appendChild(modelsHeader);
  dropdown.value = currentVal;
}

/**
 * Updates KPI Summary Cards & Models Table (Including Today's Volume)
 */
function updateKPIsAndTable(filteredData) {
  if (!filteredData || filteredData.length === 0) return;

  // The catalog view reports measured facts, so it gets its own KPI row and
  // its own table rather than the modeled volume columns.
  if (state.currentView === 'catalog') {
    renderCatalogFacts(filteredData);
    return;
  }

  if (state.currentView === 'leaderboard') {
    renderLeaderboardFacts();
    return;
  }

  restoreVolumeTableHead();

  const totalPlatformTokens = filteredData.reduce((sum, d) => sum + d.totalTokens, 0);
  const latestDay = filteredData[filteredData.length - 1]; // most recent complete week

  const modelTotals = {};
  const modelPeaks = {};
  const modelPeakDays = {};
  const modelTodayVals = {};

  TRACKED_MODELS.forEach(m => {
    modelTotals[m.slug] = 0;
    modelPeaks[m.slug] = 0;
    modelPeakDays[m.slug] = filteredData[0].date;
    modelTodayVals[m.slug] = latestDay.models[m.slug] || 0;
  });

  filteredData.forEach(day => {
    Object.entries(day.models).forEach(([slug, count]) => {
      modelTotals[slug] = (modelTotals[slug] || 0) + count;
      if (count > (modelPeaks[slug] || 0)) {
        modelPeaks[slug] = count;
        modelPeakDays[slug] = day.date;
      }
    });
  });

  let platformPeakDay = filteredData[0];
  for (const d of filteredData) {
    if (d.totalTokens > platformPeakDay.totalTokens) {
      platformPeakDay = d;
    }
  }

  // CASE 1: All Models Platform View
  if (state.selectedScope === 'all') {
    const avgWeekly = totalPlatformTokens / filteredData.length;

    let topModelSlug = '';
    let topModelTokens = -1;
    for (const [m, count] of Object.entries(modelTotals)) {
      if (m !== 'other' && count > topModelTokens) {
        topModelTokens = count;
        topModelSlug = m;
      }
    }

    // Dominant model is judged on the latest complete week, matching the table
    let topWeekSlug = '';
    let topWeekTokens = -1;
    for (const [slug, count] of Object.entries(latestDay.models)) {
      // "Others" is an aggregate bucket, not a model — it cannot be dominant
      if (slug === 'Others' || slug === 'other') continue;
      if (count > topWeekTokens) { topWeekTokens = count; topWeekSlug = slug; }
    }
    const topShare = latestDay.totalTokens > 0 ? ((topWeekTokens / latestDay.totalTokens) * 100).toFixed(1) : 0;
    const topLabel = MODEL_CONFIG[topWeekSlug]?.label || topWeekSlug;

    document.getElementById('kpi-total-label').textContent = `Tokens Over ${filteredData.length} ${granularity() === 'day' ? 'Days' : 'Weeks'}`;
    document.getElementById('kpi-total-tokens').textContent = formatTokens(totalPlatformTokens);
    document.getElementById('kpi-total-subtext').textContent = 'Prompt + completion, all models';

    document.getElementById('kpi-today-label').textContent = granularity() === 'day' ? 'Latest Day' : 'Latest Full Week';
    document.getElementById('kpi-today-tokens').textContent = formatTokens(latestDay.totalTokens);
    document.getElementById('kpi-today-subtext').textContent = granularity() === 'day' ? latestDay.date : `Week of ${latestDay.date}`;

    document.getElementById('kpi-avg-label').textContent = granularity() === 'day' ? 'Daily Average' : 'Weekly Average';
    document.getElementById('kpi-avg-tokens').textContent = formatTokens(avgWeekly) + (granularity() === 'day' ? ' / day' : ' / wk');
    document.getElementById('kpi-avg-subtext').textContent = 'Across all active models';

    document.getElementById('kpi-peak-label').textContent = granularity() === 'day' ? 'Peak Day' : 'Peak Week';
    document.getElementById('kpi-peak-tokens').textContent = formatTokens(platformPeakDay.totalTokens);
    document.getElementById('kpi-peak-date').textContent = `Peak on ${platformPeakDay.date}`;

    document.getElementById('kpi-top-label').textContent = 'Dominant Model Share';
    document.getElementById('kpi-top-model').textContent = topLabel;
    document.getElementById('kpi-top-share').textContent = `${topShare}% of ${granularity() === 'day' ? latestDay.date : 'week of ' + latestDay.date}`;

    document.getElementById('active-model-badge').style.display = 'none';

  } 
  // CASE 2: Aggregated Frontier Family Group
  else if (state.selectedScope.startsWith('group:')) {
    const grp = AGGREGATED_GROUPS[state.selectedScope];
    let groupTotal = 0;
    let groupPeak = 0;
    let groupPeakDate = filteredData[0].date;
    let groupToday = 0;

    grp.models.forEach(m => {
      groupToday += (latestDay.models[m] || 0);
    });

    filteredData.forEach(day => {
      let daySum = 0;
      grp.models.forEach(mSlug => {
        daySum += (day.models[mSlug] || 0);
      });
      groupTotal += daySum;
      if (daySum > groupPeak) {
        groupPeak = daySum;
        groupPeakDate = day.date;
      }
    });

    const grpAvg = groupTotal / filteredData.length;
    const grpShare = totalPlatformTokens > 0 ? ((groupTotal / totalPlatformTokens) * 100).toFixed(1) : 0;

    document.getElementById('kpi-total-label').textContent = `${grp.shortLabel} Total`;
    document.getElementById('kpi-total-tokens').textContent = formatTokens(groupTotal);
    document.getElementById('kpi-total-subtext').textContent = `Combined volume (${grp.models.length} models)`;

    document.getElementById('kpi-today-label').textContent = `${grp.shortLabel} Today`;
    document.getElementById('kpi-today-tokens').textContent = formatTokens(groupToday);
    document.getElementById('kpi-today-subtext').textContent = `${latestDay.date} (${((groupToday / latestDay.totalTokens) * 100).toFixed(1)}% today)`;

    document.getElementById('kpi-avg-label').textContent = `${grp.shortLabel} Weekly Avg`;
    document.getElementById('kpi-avg-tokens').textContent = formatTokens(grpAvg) + ' / wk';
    document.getElementById('kpi-avg-subtext').textContent = `Combined daily average`;

    document.getElementById('kpi-peak-label').textContent = `${grp.shortLabel} Peak Day`;
    document.getElementById('kpi-peak-tokens').textContent = formatTokens(groupPeak);
    document.getElementById('kpi-peak-date').textContent = `Peak on ${groupPeakDate}`;

    document.getElementById('kpi-top-label').textContent = `Combined Market Share`;
    document.getElementById('kpi-top-model').textContent = `${grpShare}%`;
    document.getElementById('kpi-top-share').textContent = `Of total OpenRouter volume`;

    const badge = document.getElementById('active-model-badge');
    badge.textContent = `Family: ${grp.shortLabel}`;
    badge.style.display = 'inline-block';

  } 
  // CASE 3: Single Model Drilldown
  else {
    const modelConfig = MODEL_CONFIG[state.selectedScope] || { label: state.selectedScope, provider: 'Unknown', color: TERM.accent };
    const mTotal = modelTotals[state.selectedScope] || 0;
    const mAvg = mTotal / filteredData.length;
    const mPeak = modelPeaks[state.selectedScope] || 0;
    const mPeakDate = modelPeakDays[state.selectedScope] || '-';
    const mToday = modelTodayVals[state.selectedScope] || 0;
    const mShare = totalPlatformTokens > 0 ? ((mTotal / totalPlatformTokens) * 100).toFixed(1) : 0;
    const mTodayShare = latestDay.totalTokens > 0 ? ((mToday / latestDay.totalTokens) * 100).toFixed(1) : 0;

    document.getElementById('kpi-total-label').textContent = `${modelConfig.label} 6M Volume`;
    document.getElementById('kpi-total-tokens').textContent = formatTokens(mTotal);
    document.getElementById('kpi-total-subtext').textContent = `Total tokens for ${modelConfig.label}`;

    document.getElementById('kpi-today-label').textContent = `${modelConfig.label} Today`;
    document.getElementById('kpi-today-tokens').textContent = formatTokens(mToday);
    document.getElementById('kpi-today-subtext').textContent = `${latestDay.date} (${mTodayShare}% of today's volume)`;

    document.getElementById('kpi-avg-label').textContent = `${modelConfig.label} Weekly Avg`;
    document.getElementById('kpi-avg-tokens').textContent = formatTokens(mAvg) + ' / wk';
    document.getElementById('kpi-avg-subtext').textContent = `Average daily volume`;

    document.getElementById('kpi-peak-label').textContent = `${modelConfig.label} Peak Day`;
    document.getElementById('kpi-peak-tokens').textContent = formatTokens(mPeak);
    document.getElementById('kpi-peak-date').textContent = `Peak on ${mPeakDate}`;

    document.getElementById('kpi-top-label').textContent = `Platform Market Share`;
    document.getElementById('kpi-top-model').textContent = `${mShare}%`;
    document.getElementById('kpi-top-share').textContent = `Of all OpenRouter traffic`;

    const badge = document.getElementById('active-model-badge');
    badge.textContent = `Model: ${modelConfig.label}`;
    badge.style.display = 'inline-block';
  }

  // Populate Models & Aggregations Table
  const tableBody = document.getElementById('models-table-body');
  if (!tableBody) return;
  tableBody.innerHTML = '';

  // 1. Render Aggregated Frontier Families in Table
  Object.values(AGGREGATED_GROUPS).forEach(grp => {
    let grpTotal = 0;
    let grpPeak = 0;
    let grpToday = 0;

    grp.models.forEach(m => grpToday += (latestDay.models[m] || 0));

    filteredData.forEach(day => {
      let daySum = 0;
      grp.models.forEach(m => daySum += (day.models[m] || 0));
      grpTotal += daySum;
      if (daySum > grpPeak) grpPeak = daySum;
    });

    const grpAvg = grpTotal / filteredData.length;
    // Share is measured against the latest complete week, not the whole window
    const grpShare = latestDay.totalTokens > 0 ? ((grpToday / latestDay.totalTokens) * 100).toFixed(1) : '0.0';
    const isSelected = (state.selectedScope === grp.id);

    const row = document.createElement('tr');
    row.style.backgroundColor = isSelected ? 'rgba(0, 255, 65, 0.14)' : '#101010';
    if (isSelected) row.classList.add('selected-row');

    row.innerHTML = `
      <td>
        <span class="badge" style="background: ${grp.color}26; color: ${grp.color}; border-color: ${grp.color}66;">FAMILY</span>
      </td>
      <td>
        <div class="model-name-cell">
          <span class="model-color-dot" style="background-color: ${grp.color};"></span>
          <span style="font-weight: 700; color: var(--text-primary);">${grp.label}</span>
        </div>
      </td>
      <td><span class="badge" style="font-size: 0.7rem;">${grp.provider}</span></td>
      <td class="mono-num" style="font-weight: 700; color: var(--text-primary);">${formatTokens(grpTotal)}</td>
      <td class="mono-num" style="color: var(--accent-emerald); font-weight: 600;">${formatTokens(grpToday)}</td>
      <td class="mono-num">${formatTokens(grpAvg)}</td>
      <td>
        <div class="share-bar-container">
          <span class="mono-num" style="min-width: 45px; font-weight: 600;">${grpShare}%</span>
          <div class="share-bar">
            <div class="share-bar-fill" style="width: ${grpShare}%; background-color: ${grp.color}"></div>
          </div>
        </div>
      </td>
      <td class="mono-num">${formatTokens(grpPeak)}</td>
      <td>
        <button class="btn btn-xs" style="border-color: ${grp.color}; color: ${grp.color};" data-filter-scope="${grp.id}">
          ${isSelected ? 'Selected' : 'Filter Family'}
        </button>
      </td>
    `;

    row.addEventListener('click', () => selectScope(grp.id));
    tableBody.appendChild(row);
  });

  // Table separator
  const sepRow = document.createElement('tr');
  sepRow.innerHTML = `
    <td colspan="9" style="padding: 6px 14px; background: #050505; font-size: 0.72rem; font-weight: 700; color: var(--text-muted); letter-spacing: 0.08em; text-transform: uppercase;">
      Individual Tracked Models
    </td>
  `;
  tableBody.appendChild(sepRow);

  // 2. Render individual tracked models
  const sortedSlugs = Object.keys(modelTotals).sort((a, b) => {
    if (a === 'other') return 1;
    if (b === 'other') return -1;
    return modelTotals[b] - modelTotals[a];
  });

  sortedSlugs.forEach((slug, rank) => {
    const config = MODEL_CONFIG[slug] || { label: slug, provider: 'Unknown', color: '#8c8c8c' };
    const mTotal = modelTotals[slug] || 0;
    const mToday = modelTodayVals[slug] || 0;
    const mAvg = mTotal / filteredData.length;
    const mShare = latestDay.totalTokens > 0 ? ((mToday / latestDay.totalTokens) * 100).toFixed(1) : '0.0';
    const mPeak = modelPeaks[slug] || 0;
    const isSelected = (state.selectedScope === slug);

    const isTop3 = rank < 3;
    const rankLabel = slug === 'other' ? '-' : `${rank + 1}`;

    const row = document.createElement('tr');
    if (isSelected) row.classList.add('selected-row');

    row.innerHTML = `
      <td>
        <span class="rank-badge ${isTop3 ? 'top-3' : ''}">${rankLabel}</span>
      </td>
      <td>
        <div class="model-name-cell">
          <span class="model-color-dot" style="background-color: ${config.color};"></span>
          <span>${config.label}</span>
        </div>
      </td>
      <td>${config.provider}</td>
      <td class="mono-num">${formatTokens(mTotal)}</td>
      <td class="mono-num" style="color: ${mToday > 0 ? 'var(--accent-emerald)' : 'var(--text-muted)'}; font-weight: 500;">${formatTokens(mToday)}</td>
      <td class="mono-num">${formatTokens(mAvg)}</td>
      <td>
        <div class="share-bar-container">
          <span class="mono-num" style="min-width: 45px;">${mShare}%</span>
          <div class="share-bar">
            <div class="share-bar-fill" style="width: ${mShare}%; background-color: ${config.color}"></div>
          </div>
        </div>
      </td>
      <td class="mono-num">${formatTokens(mPeak)}</td>
      <td>
        <button class="btn btn-xs" data-filter-scope="${slug}">
          ${isSelected ? 'Selected' : 'Filter'}
        </button>
      </td>
    `;

    row.addEventListener('click', () => selectScope(slug));
    tableBody.appendChild(row);
  });
}

// The volume table's header is swapped out by the catalog view, so keep a copy
const VOLUME_TABLE_HEAD = `
  <tr>
    <th>Rank</th>
    <th>Model / Aggregation</th>
    <th>Provider</th>
    <th>Window Total</th>
    <th>Latest Week</th>
    <th>Weekly Average</th>
    <th>Share (Latest Week)</th>
    <th>Peak Week</th>
    <th>Action</th>
  </tr>`;

function restoreVolumeTableHead() {
  const head = document.getElementById('models-table-head');
  if (head && head.dataset.mode !== 'volume') {
    head.innerHTML = VOLUME_TABLE_HEAD;
    head.dataset.mode = 'volume';
  }
  const heading = document.getElementById('table-dynamic-heading');
  if (heading) heading.textContent = 'Models & Provider Families (Measured Weekly Volume)';
  const subtitle = document.getElementById('table-subtitle');
  if (subtitle) {
    subtitle.textContent =
      'Measured token volume across the selected weeks, from OpenRouter\u2019s published rankings (click any row to filter).';
  }
}

/** Flags in the chart header whether the current view is measured or modeled. */
function syncMeasuredBadge() {
  const badge = document.getElementById('data-source-badge');
  if (!badge) return;
  const measured = state.currentView === 'catalog' || state.currentView === 'leaderboard';
  badge.textContent = state.currentView === 'catalog' ? 'Measured — catalog'
    : state.currentView === 'leaderboard' ? 'Measured — per model'
    : state.currentView === 'daily' ? 'Measured — daily (key)'
    : 'Measured — rankings';
  badge.classList.add('live');
}

/**
 * KPI row + table for the Latest Week view, from the per-model endpoint —
 * the one source that covers every model rather than the weekly top nine.
 */
function renderLeaderboardFacts() {
  const leaders = CATALOG.leaders;
  if (!leaders.length) return;

  const totalTokens = leaders.reduce((s, l) => s + l.totalTokens, 0);
  const totalRequests = leaders.reduce((s, l) => s + l.requests, 0);
  const top = leaders[0];
  const vendors = new Set(leaders.map(l => l.provider));
  const completion = leaders.reduce((s, l) => s + l.completionTokens, 0);

  const set = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text; };

  set('kpi-total-label', 'Tokens This Week');
  set('kpi-total-tokens', formatTokens(totalTokens));
  set('kpi-total-subtext', `${leaders.length} models across ${vendors.size} vendors`);

  set('kpi-today-label', 'Leading Model');
  set('kpi-today-tokens', top.label);
  set('kpi-today-subtext', `${formatTokens(top.totalTokens)} · ${((top.totalTokens / totalTokens) * 100).toFixed(1)}% of tracked`);

  set('kpi-avg-label', 'Requests This Week');
  set('kpi-avg-tokens', fmtInt(totalRequests));
  set('kpi-avg-subtext', `${fmtInt(Math.round(totalTokens / Math.max(totalRequests, 1)))} tokens per request`);

  set('kpi-peak-label', 'Completion Share');
  set('kpi-peak-tokens', ((completion / totalTokens) * 100).toFixed(1) + '%');
  set('kpi-peak-date', 'Output vs prompt tokens');

  set('kpi-top-label', 'Week Of');
  set('kpi-top-model', CATALOG.leaderboardWeek || CATALOG.asOf);
  set('kpi-top-share', 'Per-model endpoint');

  const badge = document.getElementById('active-model-badge');
  if (badge) badge.style.display = 'none';

  const head = document.getElementById('models-table-head');
  if (head) {
    head.dataset.mode = 'leaderboard';
    head.innerHTML = `
      <tr>
        <th>#</th>
        <th>Model</th>
        <th>Provider</th>
        <th>Total Tokens</th>
        <th>Prompt</th>
        <th>Completion</th>
        <th>Requests</th>
        <th>Share</th>
      </tr>`;
  }

  const heading = document.getElementById('table-dynamic-heading');
  if (heading) heading.textContent = `Every Model With Traffic — Week of ${CATALOG.leaderboardWeek}`;
  const subtitle = document.getElementById('table-subtitle');
  if (subtitle) {
    subtitle.textContent =
      'From OpenRouter’s per-model endpoint, which covers the whole roster — including models the weekly top-9 series folds into "Others".';
  }

  const body = document.getElementById('models-table-body');
  if (!body) return;
  body.innerHTML = '';

  leaders.forEach((l, i) => {
    const ramp = PROVIDER_PALETTE[l.provider] || PROVIDER_PALETTE.Unknown;
    const share = ((l.totalTokens / totalTokens) * 100).toFixed(1);
    const row = document.createElement('tr');
    row.innerHTML = `
      <td><span class="rank-badge ${i < 3 ? 'top-3' : ''}">${i + 1}</span></td>
      <td>
        <div class="model-name-cell">
          <span class="model-color-dot" style="background-color: ${ramp[0]};"></span>
          <span>${l.label}</span>
        </div>
      </td>
      <td>${l.provider}</td>
      <td class="mono-num" style="color: var(--text-primary); font-weight: 600;">${formatTokens(l.totalTokens)}</td>
      <td class="mono-num">${formatTokens(l.promptTokens)}</td>
      <td class="mono-num">${formatTokens(l.completionTokens)}</td>
      <td class="mono-num">${fmtInt(l.requests)}</td>
      <td>
        <div class="share-bar-container">
          <span class="mono-num" style="min-width: 42px;">${share}%</span>
          <div class="share-bar">
            <div class="share-bar-fill" style="width: ${Math.min(Number(share) * 3, 100)}%; background-color: ${ramp[0]}"></div>
          </div>
        </div>
      </td>
    `;
    row.style.cursor = 'default';
    body.appendChild(row);
  });
}

/**
 * KPI row + table for the catalog view. Every figure below is read from
 * OpenRouter's catalog — counts, dates, context windows and list prices.
 */
function renderCatalogFacts(filteredData) {
  const windowStart = filteredData[0].date;
  const all = CATALOG.catalogModels;
  const inWindow = all.filter(m => m.released >= windowStart);
  const newest = all[all.length - 1];
  const vendors = new Set(all.map(m => m.provider));

  // openrouter/* entries are routers rather than models, so they do not
  // represent a real context window
  const realModels = all.filter(m => !m.slug.startsWith('openrouter/'));
  const widest = realModels.reduce((best, m) => (m.context || 0) > (best.context || 0) ? m : best, realModels[0]);
  const priced = all.filter(m => m.promptPrice !== null).map(m => m.promptPrice).sort((a, b) => a - b);
  const medianPrice = priced.length ? priced[Math.floor(priced.length / 2)] : null;

  const set = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text; };

  set('kpi-total-label', 'Models in Catalog');
  set('kpi-total-tokens', fmtInt(all.length));
  set('kpi-total-subtext', `${vendors.size} vendors represented`);

  set('kpi-today-label', `Released in ${filteredData.length} Days`);
  set('kpi-today-tokens', fmtInt(inWindow.length));
  set('kpi-today-subtext', `Since ${windowStart}`);

  set('kpi-avg-label', 'Newest Release');
  set('kpi-avg-tokens', newest.label);
  set('kpi-avg-subtext', `${newest.provider} · ${newest.released}`);

  set('kpi-peak-label', 'Largest Context Window');
  set('kpi-peak-tokens', widest.context >= 1e6
    ? (widest.context / 1e6).toFixed(1).replace(/\.0$/, '') + 'M'
    : fmtInt(widest.context));
  set('kpi-peak-date', widest.label);

  set('kpi-top-label', 'Median Prompt Price');
  set('kpi-top-model', medianPrice !== null ? `$${medianPrice.toFixed(2)}` : '—');
  set('kpi-top-share', 'Per million tokens (list)');

  const badge = document.getElementById('active-model-badge');
  if (badge) badge.style.display = 'none';

  const subtitle = document.getElementById('table-subtitle');
  if (subtitle) {
    subtitle.textContent =
      'Release date, context window and list price for every model published in the window — read from the catalog, not modeled.';
  }

  // Table: the real releases inside the charted window, newest first
  const head = document.getElementById('models-table-head');
  if (head) {
    head.dataset.mode = 'catalog';
    head.innerHTML = `
      <tr>
        <th>#</th>
        <th>Model</th>
        <th>Provider</th>
        <th>Released</th>
        <th>Context</th>
        <th>Prompt $/Mtok</th>
        <th>Completion $/Mtok</th>
        <th>Slug</th>
      </tr>`;
  }

  const heading = document.getElementById('table-dynamic-heading');
  if (heading) heading.textContent = `Releases in the Last ${filteredData.length} Days (Measured)`;

  const body = document.getElementById('models-table-body');
  if (!body) return;
  body.innerHTML = '';

  const colorFor = p => {
    const ramp = PROVIDER_PALETTE[p] || PROVIDER_PALETTE.Unknown;
    return ramp[0];
  };

  [...inWindow].reverse().forEach((m, i) => {
    const row = document.createElement('tr');
    row.innerHTML = `
      <td><span class="rank-badge ${i < 3 ? 'top-3' : ''}">${i + 1}</span></td>
      <td>
        <div class="model-name-cell">
          <span class="model-color-dot" style="background-color: ${colorFor(m.provider)};"></span>
          <span>${m.label}</span>
        </div>
      </td>
      <td>${m.provider}</td>
      <td class="mono-num">${m.released}</td>
      <td class="mono-num">${m.context ? fmtInt(m.context) : '—'}</td>
      <td class="mono-num">${m.promptPrice !== null ? '$' + m.promptPrice.toFixed(2) : '—'}</td>
      <td class="mono-num">${m.completionPrice !== null ? '$' + m.completionPrice.toFixed(2) : '—'}</td>
      <td class="mono-num" style="color: var(--text-muted);">${m.slug}</td>
    `;
    if (MODEL_CONFIG[m.slug]) {
      row.addEventListener('click', () => {
        state.currentView = 'total';
        document.querySelectorAll('.seg-btn').forEach(b => b.classList.toggle('active', b.dataset.view === 'total'));
        selectScope(m.slug);
      });
    } else {
      row.style.cursor = 'default';
    }
    body.appendChild(row);
  });
}

/**
 * Change Scope Selection
 */
function selectScope(scopeId) {
  state.selectedScope = scopeId;
  const dropdown = document.getElementById('model-filter-select');
  if (dropdown) dropdown.value = scopeId;
  renderChart();
}

/**
 * ===========================================================================
 * D3 CHART LAYER
 * ===========================================================================
 * buildChartSpec() reduces the current state into a renderer-agnostic spec:
 *   { xKind, xValues, layers[], stackMode, yTitle, yKind }
 * renderChart() draws that spec as SVG — grid, axes, stacked areas / lines /
 * bars, crosshair with a unified tooltip, clickable legend and the
 * scope-aware release milestones along the X-axis.
 */

const CHART_HEIGHT = 560;
const CHART_MARGIN = { top: 24, right: 26, bottom: 84, left: 76 };

const fmtAxisDay = utcFormat('%b %d');
const fmtLongDay = utcFormat('%a %d %b %Y');
const fmtInt = d3Format(',.0f');
const fmtSI = d3Format('~s');

// Series visibility is toggled from the legend and reset when the layer set changes
let hiddenSeries = new Set();
let lastLayerKey = '';

function parseDay(iso) {
  return new Date(iso + 'T00:00:00Z');
}

function layer(opts) {
  return {
    id: opts.id,
    label: opts.label,
    color: opts.color,
    values: opts.values,
    raw: opts.raw || null,
    kind: opts.kind || 'area',
    stacked: !!opts.stacked,
    dash: !!opts.dash,
    width: opts.width || 2,
    fillOpacity: opts.fillOpacity !== undefined ? opts.fillOpacity : 0.85
  };
}

function monthlyBuckets(dataSlice) {
  const map = {};
  dataSlice.forEach(d => {
    const key = d.date.substring(0, 7);
    if (!map[key]) map[key] = { total: 0, models: {} };
    map[key].total += d.totalTokens;
    Object.entries(d.models).forEach(([slug, count]) => {
      map[key].models[slug] = (map[key].models[slug] || 0) + count;
    });
  });
  const keys = Object.keys(map).sort();
  const labels = keys.map(k => {
    const [yr, mo] = k.split('-');
    return new Date(yr, parseInt(mo, 10) - 1, 1).toLocaleString('default', { month: 'short', year: 'numeric' });
  });
  return { map, keys, labels };
}

/**
 * Builds the chart spec for the active scope + view, and sets the chart title.
 */
function buildChartSpec(dataSlice) {
  const mult = getUnitMultiplier();
  const unitSuffix = getUnitSuffix();
  const titleEl = document.getElementById('chart-dynamic-title');
  const setTitle = txt => { if (titleEl) titleEl.textContent = txt; };

  const dates = dataSlice.map(d => parseDay(d.date));
  const totalTokens = dataSlice.map(d => d.totalTokens);

  const spec = {
    xKind: 'time',
    xValues: dates,
    layers: [],
    stackMode: 'none', // 'none' | 'normal' | 'percent'
    yTitle: unitSuffix.toUpperCase(),
    yKind: 'unit'
  };

  // -------------------------------------------------------------------------
  // VIEW: CATALOG GROWTH — measured, not modeled
  // -------------------------------------------------------------------------
  // Every point here is read from OpenRouter's catalog: a model counts from
  // the day it was published. No weights, no ramps, no PRNG.
  // -------------------------------------------------------------------------
  if (state.currentView === 'catalog') {
    setTitle(`Models Live on OpenRouter (Measured — ${CATALOG.catalogSize} in Catalog)`);
    spec.stackMode = 'normal';
    spec.yKind = 'count';
    spec.yTitle = 'MODELS AVAILABLE';
    spec.measured = true;

    const vendors = CATALOG.topVendors;
    const buckets = new Map(vendors.map(v => [v, []]));
    buckets.set('Other vendors', []);
    const bucketFor = v => (buckets.has(v) ? v : 'Other vendors');

    dates.forEach(day => {
      const cutoff = day.toISOString().slice(0, 10);
      const counts = {};
      CATALOG.catalogModels.forEach(m => {
        if (m.released <= cutoff) {
          const b = bucketFor(m.provider);
          counts[b] = (counts[b] || 0) + 1;
        }
      });
      buckets.forEach((arr, name) => arr.push(counts[name] || 0));
    });

    buckets.forEach((values, name) => {
      const ramp = PROVIDER_PALETTE[name] || PROVIDER_PALETTE.Unknown;
      spec.layers.push(layer({
        id: `vendor:${name}`,
        label: name,
        color: name === 'Other vendors' ? '#3a3a3a' : ramp[0],
        values,
        stacked: true
      }));
    });

    return spec;
  }

  // -------------------------------------------------------------------------
  // VIEW: DAILY — credentialed source, top 50 models per day
  // -------------------------------------------------------------------------
  if (state.currentView === 'daily') {
    setTitle(`Daily Token Volume — ${CATALOG.daily.start} to ${CATALOG.daily.end} (Measured)`);
    spec.stackMode = 'normal';
    spec.measured = true;

    // Rank by volume across the window and stack the leaders; everything else
    // joins the aggregate bucket so the legend stays legible.
    const totals = {};
    dataSlice.forEach(d => {
      Object.entries(d.models).forEach(([slug, tokens]) => {
        totals[slug] = (totals[slug] || 0) + tokens;
      });
    });

    const ranked = Object.keys(totals)
      .filter(s => s !== 'Others')
      .sort((a, b) => totals[b] - totals[a]);
    const leaders = ranked.slice(0, 12);
    const rest = new Set(ranked.slice(12));

    leaders.forEach(slug => {
      const cfg = DAILY_MODEL_CONFIG[slug] || { label: slug, color: '#6e6e6e' };
      const raw = dataSlice.map(d => d.models[slug] || 0);
      spec.layers.push(layer({
        id: slug,
        label: cfg.label,
        color: cfg.color,
        values: raw.map(v => v / mult),
        raw,
        stacked: true
      }));
    });

    const otherRaw = dataSlice.map(d => {
      let sum = d.models.Others || 0;
      rest.forEach(slug => { sum += d.models[slug] || 0; });
      return sum;
    });
    spec.layers.push(layer({
      id: 'daily-other',
      label: `All Other Models (${rest.size} tracked + long tail)`,
      color: '#3a3a3a',
      values: otherRaw.map(v => v / mult),
      raw: otherRaw,
      stacked: true
    }));

    return spec;
  }

  // -------------------------------------------------------------------------
  // VIEW: LATEST WEEK — every model, not just the weekly top nine
  // -------------------------------------------------------------------------
  // The weekly series only publishes 9 models plus "Others" per week, so a
  // model with real traffic vanishes into the bucket on weeks it did not
  // place. This view reads the per-model endpoint instead, which covers the
  // whole roster for the most recent week.
  // -------------------------------------------------------------------------
  if (state.currentView === 'leaderboard') {
    setTitle(`Every Model by Token Volume — Week of ${CATALOG.leaderboardWeek} (Measured)`);
    spec.xKind = 'band';
    spec.measured = true;

    const top = CATALOG.leaders.slice(0, 24);
    spec.xValues = top.map(l => l.label);
    spec.stackMode = 'normal';

    const promptVals = top.map(l => l.promptTokens / mult);
    const completionVals = top.map(l => l.completionTokens / mult);

    spec.layers.push(layer({
      id: 'prompt',
      label: 'Prompt tokens',
      color: TERM.accent,
      values: promptVals,
      raw: top.map(l => l.promptTokens),
      kind: 'bar',
      stacked: true
    }));
    spec.layers.push(layer({
      id: 'completion',
      label: 'Completion tokens',
      color: '#4d9dff',
      values: completionVals,
      raw: top.map(l => l.completionTokens),
      kind: 'bar',
      stacked: true
    }));

    return spec;
  }

  // -------------------------------------------------------------------------
  // VIEW: FRONTIER FAMILIES (platform-wide family race)
  // -------------------------------------------------------------------------
  if (state.currentView === 'families') {
    setTitle(`Provider Families by Weekly Token Volume (Measured, through ${dataSlice[dataSlice.length - 1].date})`);
    spec.stackMode = 'normal';

    const accounted = new Set();
    Object.values(AGGREGATED_GROUPS).forEach(grp => {
      grp.models.forEach(s => accounted.add(s));
      const raw = dataSlice.map(d => grp.models.reduce((sum, s) => sum + (d.models[s] || 0), 0));
      spec.layers.push(layer({
        id: grp.id,
        label: grp.shortLabel,
        color: grp.color,
        values: raw.map(v => v / mult),
        raw,
        stacked: true
      }));
    });

    const otherRaw = dataSlice.map(d => {
      let sum = 0;
      Object.entries(d.models).forEach(([s, count]) => { if (!accounted.has(s)) sum += count; });
      return sum;
    });
    spec.layers.push(layer({
      id: 'other-families',
      label: 'Preceding & Other Models',
      color: '#3a3a3a',
      values: otherRaw.map(v => v / mult),
      raw: otherRaw,
      stacked: true
    }));

    return spec;
  }

  // -------------------------------------------------------------------------
  // SCOPE: AGGREGATED FAMILY GROUP
  // -------------------------------------------------------------------------
  if (state.selectedScope.startsWith('group:')) {
    const grp = AGGREGATED_GROUPS[state.selectedScope];
    const grpRaw = dataSlice.map(d => grp.models.reduce((sum, s) => sum + (d.models[s] || 0), 0));

    if (state.currentView === 'total') {
      setTitle(`${grp.shortLabel} — Combined Weekly Token Volume`);
      const values = grpRaw.map(v => v / mult);
      spec.layers.push(layer({
        id: grp.id,
        label: `${grp.shortLabel} Total`,
        color: grp.color,
        values,
        raw: grpRaw,
        kind: 'area',
        fillOpacity: 0.14,
        width: 2.2
      }));
      if (state.showMA) {
        spec.layers.push(layer({
          id: 'ma',
          label: '4-Week Moving Avg',
          color: '#d4d4d4',
          values: calculateMovingAverage(values, 4),
          kind: 'line',
          dash: true,
          width: 1.6
        }));
      }
      return spec;
    }

    if (state.currentView === 'stacked') {
      setTitle(`${grp.shortLabel} — Internal Model Breakdown`);
      spec.stackMode = 'normal';
      grp.models.forEach(slug => {
        const cfg = MODEL_CONFIG[slug] || { label: slug, color: grp.color };
        const raw = dataSlice.map(d => d.models[slug] || 0);
        spec.layers.push(layer({
          id: slug,
          label: cfg.label,
          color: cfg.color,
          values: raw.map(v => v / mult),
          raw,
          stacked: true
        }));
      });
      return spec;
    }

    if (state.currentView === 'market-share') {
      setTitle(`${grp.shortLabel} — Platform Market Share (%)`);
      spec.yKind = 'percent';
      spec.yTitle = 'PLATFORM MARKET SHARE (%)';
      const shares = dataSlice.map((d, i) => d.totalTokens > 0 ? (grpRaw[i] / d.totalTokens) * 100 : 0);
      spec.layers.push(layer({
        id: grp.id,
        label: `${grp.shortLabel} Share`,
        color: grp.color,
        values: shares,
        raw: grpRaw,
        kind: 'area',
        fillOpacity: 0.16,
        width: 2.2
      }));
      if (state.showMA) {
        spec.layers.push(layer({
          id: 'ma',
          label: '4-Week Avg Share',
          color: '#d4d4d4',
          values: calculateMovingAverage(shares, 4),
          kind: 'line',
          dash: true,
          width: 1.6
        }));
      }
      return spec;
    }

    // monthly
    setTitle(`${grp.shortLabel} — Monthly Aggregated Volume`);
    const { map, keys, labels } = monthlyBuckets(dataSlice);
    spec.xKind = 'band';
    spec.xValues = labels;
    spec.stackMode = 'normal';
    grp.models.forEach(slug => {
      const cfg = MODEL_CONFIG[slug] || { label: slug, color: grp.color };
      const raw = keys.map(k => map[k].models[slug] || 0);
      spec.layers.push(layer({
        id: slug,
        label: cfg.label,
        color: cfg.color,
        values: raw.map(v => v / mult),
        raw,
        kind: 'bar',
        stacked: true
      }));
    });
    return spec;
  }

  // -------------------------------------------------------------------------
  // SCOPE: SINGLE MODEL DRILLDOWN
  // -------------------------------------------------------------------------
  if (state.selectedScope !== 'all') {
    const cfg = MODEL_CONFIG[state.selectedScope] || { label: state.selectedScope, color: TERM.accent };
    const raw = dataSlice.map(d => d.models[state.selectedScope] || 0);
    const values = raw.map(v => v / mult);
    const shares = dataSlice.map((d, i) => d.totalTokens > 0 ? (raw[i] / d.totalTokens) * 100 : 0);

    if (state.currentView === 'total') {
      setTitle(`${cfg.label} — Weekly Token Volume & Trend`);
      spec.layers.push(layer({
        id: state.selectedScope,
        label: `${cfg.label} Weekly`,
        color: cfg.color,
        values,
        raw,
        kind: 'area',
        fillOpacity: 0.14,
        width: 2.2
      }));
      if (state.showMA) {
        spec.layers.push(layer({
          id: 'ma',
          label: '4-Week Moving Avg',
          color: '#d4d4d4',
          values: calculateMovingAverage(values, 4),
          kind: 'line',
          dash: true,
          width: 1.6
        }));
      }
      return spec;
    }

    if (state.currentView === 'stacked' || state.currentView === 'families') {
      setTitle(`${cfg.label} vs Rest of OpenRouter Platform`);
      spec.stackMode = 'normal';
      const restRaw = dataSlice.map((d, i) => d.totalTokens - raw[i]);
      spec.layers.push(layer({
        id: state.selectedScope,
        label: cfg.label,
        color: cfg.color,
        values,
        raw,
        stacked: true
      }));
      spec.layers.push(layer({
        id: 'rest',
        label: 'All Other Models',
        color: '#3a3a3a',
        values: restRaw.map(v => v / mult),
        raw: restRaw,
        stacked: true
      }));
      return spec;
    }

    if (state.currentView === 'market-share') {
      setTitle(`${cfg.label} — Platform Market Share (%)`);
      spec.yKind = 'percent';
      spec.yTitle = `${cfg.label.toUpperCase()} MARKET SHARE (%)`;
      spec.layers.push(layer({
        id: state.selectedScope,
        label: `${cfg.label} % Share`,
        color: cfg.color,
        values: shares,
        raw,
        kind: 'area',
        fillOpacity: 0.16,
        width: 2.2
      }));
      if (state.showMA) {
        spec.layers.push(layer({
          id: 'ma',
          label: '4-Week Avg Share',
          color: '#d4d4d4',
          values: calculateMovingAverage(shares, 4),
          kind: 'line',
          dash: true,
          width: 1.6
        }));
      }
      return spec;
    }

    // monthly
    setTitle(`${cfg.label} — Monthly Token Volume`);
    const { map, keys, labels } = monthlyBuckets(dataSlice);
    const mRaw = keys.map(k => map[k].models[state.selectedScope] || 0);
    spec.xKind = 'band';
    spec.xValues = labels;
    spec.stackMode = 'normal';
    spec.layers.push(layer({
      id: state.selectedScope,
      label: cfg.label,
      color: cfg.color,
      values: mRaw.map(v => v / mult),
      raw: mRaw,
      kind: 'bar',
      stacked: true
    }));
    return spec;
  }

  // -------------------------------------------------------------------------
  // SCOPE: ALL MODELS (PLATFORM)
  // -------------------------------------------------------------------------
  if (state.currentView === 'total') {
    setTitle(`OpenRouter Weekly Token Volume (Measured, through ${dataSlice[dataSlice.length - 1].date})`);
    const values = totalTokens.map(v => v / mult);
    spec.layers.push(layer({
      id: 'platform-total',
      label: 'Weekly Total Tokens',
      color: TERM.accent,
      values,
      raw: totalTokens,
      kind: 'area',
      fillOpacity: 0.12,
      width: 2
    }));
    if (state.showMA) {
      spec.layers.push(layer({
        id: 'ma',
        label: '4-Week Moving Avg',
        color: '#d4d4d4',
        values: calculateMovingAverage(values, 4),
        kind: 'line',
        dash: true,
        width: 1.6
      }));
    }
    return spec;
  }

  if (state.currentView === 'stacked' || state.currentView === 'market-share') {
    const isShare = state.currentView === 'market-share';
    setTitle(isShare ? 'Weekly Market Share (%)' : 'Weekly Token Volume by Model (Top 9 Each Week)');
    spec.stackMode = isShare ? 'percent' : 'normal';
    if (isShare) {
      spec.yKind = 'percent';
      spec.yTitle = 'SHARE OF DAILY TOKEN TRAFFIC (%)';
    }

    const majorModels = TRACKED_MODELS.slice(0, 14);
    const majorSlugs = new Set(majorModels.map(m => m.slug));

    majorModels.forEach(m => {
      const raw = dataSlice.map(d => d.models[m.slug] || 0);
      spec.layers.push(layer({
        id: m.slug,
        label: m.label,
        color: m.color,
        values: raw.map(v => v / mult),
        raw,
        stacked: true
      }));
    });

    const restRaw = dataSlice.map(d => {
      let sum = 0;
      Object.entries(d.models).forEach(([s, count]) => { if (!majorSlugs.has(s)) sum += count; });
      return sum;
    });
    spec.layers.push(layer({
      id: 'rest',
      label: 'Remaining 36+ Models & Other',
      color: '#3a3a3a',
      values: restRaw.map(v => v / mult),
      raw: restRaw,
      stacked: true
    }));
    return spec;
  }

  // monthly
  setTitle('Monthly Aggregated Token Usage');
  const { map, keys, labels } = monthlyBuckets(dataSlice);
  spec.xKind = 'band';
  spec.xValues = labels;
  spec.stackMode = 'normal';
  TRACKED_MODELS.slice(0, 12).forEach(m => {
    const raw = keys.map(k => map[k].models[m.slug] || 0);
    spec.layers.push(layer({
      id: m.slug,
      label: m.label,
      color: m.color,
      values: raw.map(v => v / mult),
      raw,
      kind: 'bar',
      stacked: true
    }));
  });
  return spec;
}

/**
 * Milestones visible for the current scope inside the plotted date window.
 */
function visibleMilestones(dataSlice) {
  if (!state.showEvents || state.currentView === 'monthly') return [];

  let target = FRONTIER_RELEASE_MILESTONES;
  if (state.selectedScope.startsWith('group:')) {
    const slugs = new Set(AGGREGATED_GROUPS[state.selectedScope].models);
    target = target.filter(m => slugs.has(m.slug));
  } else if (state.selectedScope !== 'all') {
    target = target.filter(m => m.slug === state.selectedScope);
  }

  const minDate = dataSlice[0].date;
  const maxDate = dataSlice[dataSlice.length - 1].date;
  return target.filter(m => m.releaseDate >= minDate && m.releaseDate <= maxDate);
}

function formatValue(v, spec) {
  if (spec.yKind === 'percent') return v.toFixed(1) + '%';
  if (spec.yKind === 'count') return fmtInt(v) + (Math.round(v) === 1 ? ' model' : ' models');
  if (state.unit === 'raw') return fmtInt(v);
  return v.toFixed(2) + ' ' + state.unit;
}

/**
 * Main render — rebuilds the SVG from the current state.
 */
function renderChart() {
  const container = document.getElementById('chart-root');
  if (!container) return;

  const source = activeDataset();
  let dataSlice = source;
  // The range buttons are sized in weeks; the daily series is only 30 days
  // long, so it always shows in full rather than being sliced by them.
  if (state.currentView !== 'daily' && state.activeWeeks !== 'all') {
    dataSlice = source.slice(-parseInt(state.activeWeeks, 10));
  }
  if (!dataSlice.length) return;

  updateKPIsAndTable(dataSlice);

  const spec = buildChartSpec(dataSlice);

  // Reset legend toggles whenever the series set changes
  const layerKey = spec.layers.map(l => l.id).join('|');
  if (layerKey !== lastLayerKey) {
    hiddenSeries = new Set();
    lastLayerKey = layerKey;
  }

  const width = Math.max(container.clientWidth || 900, 320);
  const height = CHART_HEIGHT;
  const inner = {
    w: Math.max(width - CHART_MARGIN.left - CHART_MARGIN.right, 100),
    h: height - CHART_MARGIN.top - CHART_MARGIN.bottom
  };

  container.innerHTML = '';
  container.style.position = 'relative';

  const svg = select(container)
    .append('svg')
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', `0 0 ${width} ${height}`)
    .attr('font-family', TERM.font)
    .style('display', 'block');

  const root = svg.append('g').attr('transform', `translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`);

  // Plot background
  root.append('rect')
    .attr('width', inner.w)
    .attr('height', inner.h)
    .attr('fill', TERM.bgInset)
    .attr('stroke', TERM.axis);

  // ---- Scales -------------------------------------------------------------
  const isBand = spec.xKind === 'band';
  const xBand = isBand
    ? scaleBand().domain(spec.xValues).range([0, inner.w]).padding(0.28)
    : null;
  const xTime = !isBand
    ? scaleUtc().domain([spec.xValues[0], spec.xValues[spec.xValues.length - 1]]).range([0, inner.w])
    : null;
  const xAt = i => isBand ? xBand(spec.xValues[i]) + xBand.bandwidth() / 2 : xTime(spec.xValues[i]);

  const activeLayers = spec.layers.filter(l => !hiddenSeries.has(l.id));
  const stackedLayers = activeLayers.filter(l => l.stacked);
  const looseLayers = activeLayers.filter(l => !l.stacked);
  const isPercentStack = spec.stackMode === 'percent';

  let stackSeries = [];
  if (stackedLayers.length) {
    const rows = spec.xValues.map((_, i) => {
      const row = {};
      stackedLayers.forEach(l => { row[l.id] = l.values[i] || 0; });
      return row;
    });
    stackSeries = d3Stack()
      .keys(stackedLayers.map(l => l.id))
      .order(stackOrderNone)
      .offset(isPercentStack ? stackOffsetExpand : stackOffsetNone)(rows);

    if (isPercentStack) {
      stackSeries.forEach(s => s.forEach(p => {
        p[0] = (p[0] || 0) * 100;
        p[1] = (p[1] || 0) * 100;
      }));
    }
  }

  const stackMax = stackSeries.length ? d3Max(stackSeries[stackSeries.length - 1], p => p[1]) || 0 : 0;
  const looseMax = d3Max(looseLayers, l => d3Max(l.values)) || 0;
  const yMax = Math.max(stackMax, looseMax) || 1;

  const useLog = state.yScale === 'log' && !isPercentStack;
  let yScale;
  if (useLog) {
    const positives = [];
    activeLayers.forEach(l => l.values.forEach(v => { if (v > 0) positives.push(v); }));
    const lo = positives.length ? (d3Min(positives) || 1) * 0.7 : 1;
    yScale = scaleLog().domain([lo, yMax * 1.1]).range([inner.h, 0]).clamp(true);
  } else {
    yScale = scaleLinear()
      .domain([0, isPercentStack ? 100 : yMax * 1.08])
      .range([inner.h, 0])
      .nice(isPercentStack ? undefined : 6);
    if (isPercentStack) yScale.domain([0, 100]);
  }
  const yBase = yScale.range()[0];

  // ---- Grid ---------------------------------------------------------------
  const yTicks = useLog ? yScale.ticks(6) : yScale.ticks(6);
  root.append('g')
    .selectAll('line')
    .data(yTicks)
    .join('line')
    .attr('x1', 0)
    .attr('x2', inner.w)
    .attr('y1', d => yScale(d))
    .attr('y2', d => yScale(d))
    .attr('stroke', TERM.grid)
    .attr('shape-rendering', 'crispEdges');

  if (!isBand) {
    const xTicks = xTime.ticks(Math.max(Math.floor(inner.w / 110), 2));
    root.append('g')
      .selectAll('line')
      .data(xTicks)
      .join('line')
      .attr('y1', 0)
      .attr('y2', inner.h)
      .attr('x1', d => xTime(d))
      .attr('x2', d => xTime(d))
      .attr('stroke', TERM.grid)
      .attr('shape-rendering', 'crispEdges');
  }

  // ---- Series -------------------------------------------------------------
  const plot = root.append('g').attr('clip-path', 'url(#chart-clip)');
  svg.append('defs')
    .append('clipPath')
    .attr('id', 'chart-clip')
    .append('rect')
    .attr('width', inner.w)
    .attr('height', inner.h);

  if (isBand && stackSeries.length) {
    // Stacked bars
    stackSeries.forEach((series, si) => {
      const l = stackedLayers[si];
      plot.append('g')
        .selectAll('rect')
        .data(series)
        .join('rect')
        .attr('x', (_, i) => xBand(spec.xValues[i]))
        .attr('width', xBand.bandwidth())
        .attr('y', p => yScale(Math.max(p[1], yScale.domain()[0])))
        .attr('height', p => Math.max(yScale(Math.max(p[0], yScale.domain()[0])) - yScale(Math.max(p[1], yScale.domain()[0])), 0))
        .attr('fill', l.color)
        .attr('fill-opacity', 0.9)
        .attr('stroke', TERM.bgInset)
        .attr('stroke-width', 0.5);
    });
  } else if (stackSeries.length) {
    // Stacked areas
    const areaGen = d3Area()
      .x((_, i) => xAt(i))
      .y0(p => yScale(Math.max(p[0], yScale.domain()[0])))
      .y1(p => yScale(Math.max(p[1], yScale.domain()[0])));

    stackSeries.forEach((series, si) => {
      const l = stackedLayers[si];
      plot.append('path')
        .attr('d', areaGen(series))
        .attr('fill', l.color)
        .attr('fill-opacity', 0.82)
        .attr('stroke', l.color)
        .attr('stroke-width', 0.8);
    });
  }

  // Non-stacked areas and lines
  looseLayers.forEach(l => {
    if (l.kind === 'area') {
      const areaGen = d3Area()
        .x((_, i) => xAt(i))
        .y0(yBase)
        .y1(v => yScale(Math.max(v, yScale.domain()[0])));
      plot.append('path')
        .attr('d', areaGen(l.values))
        .attr('fill', l.color)
        .attr('fill-opacity', l.fillOpacity);
    }
    const lineGen = d3Line()
      .x((_, i) => xAt(i))
      .y(v => yScale(Math.max(v, yScale.domain()[0])));
    plot.append('path')
      .attr('d', lineGen(l.values))
      .attr('fill', 'none')
      .attr('stroke', l.color)
      .attr('stroke-width', l.width)
      .attr('stroke-dasharray', l.dash ? '5 4' : null)
      .attr('stroke-linejoin', 'round');
  });

  // ---- Axes ---------------------------------------------------------------
  const yAxis = axisLeft(yScale)
    .ticks(6)
    .tickFormat(d => {
      if (spec.yKind === 'percent') return d + '%';
      if (spec.yKind === 'count') return Number.isInteger(d) ? fmtInt(d) : '';
      if (state.unit === 'raw' || useLog) return fmtSI(d);
      return d3Format('~f')(d);
    })
    .tickSize(4);

  const yAxisG = root.append('g').call(yAxis);
  yAxisG.select('.domain').attr('stroke', TERM.axis);
  yAxisG.selectAll('line').attr('stroke', TERM.axis);
  yAxisG.selectAll('text')
    .attr('fill', TERM.textSecondary)
    .attr('font-size', 10)
    .attr('font-family', TERM.font);

  const xAxis = isBand
    ? axisBottom(xBand).tickSize(4)
    : axisBottom(xTime).ticks(Math.max(Math.floor(inner.w / 110), 2)).tickFormat(fmtAxisDay).tickSize(4);

  const xAxisG = root.append('g').attr('transform', `translate(0,${inner.h})`).call(xAxis);
  xAxisG.select('.domain').attr('stroke', TERM.axis);
  xAxisG.selectAll('line').attr('stroke', TERM.axis);
  xAxisG.selectAll('text')
    .attr('fill', TERM.textSecondary)
    .attr('font-size', 10)
    .attr('font-family', TERM.font)
    .attr('text-anchor', 'end')
    .attr('dx', '-0.5em')
    .attr('dy', '0.3em')
    .attr('transform', 'rotate(-45)');

  // Y axis title
  root.append('text')
    .attr('transform', `rotate(-90)`)
    .attr('x', -inner.h / 2)
    .attr('y', -CHART_MARGIN.left + 16)
    .attr('text-anchor', 'middle')
    .attr('fill', TERM.textMuted)
    .attr('font-size', 10)
    .attr('letter-spacing', '0.12em')
    .text(spec.yTitle);

  // ---- Tooltip ------------------------------------------------------------
  const tooltip = select(container)
    .append('div')
    .attr('class', 'chart-tooltip')
    .style('opacity', 0);

  // ---- Release milestones -------------------------------------------------
  const milestones = isBand ? [] : visibleMilestones(dataSlice);

  // Labels are rotated -45°, so each one needs horizontal room within its tier;
  // clustered releases get pushed up to the next free tier instead of overlapping.
  // Each tier also steps left, so neighbouring labels separate perpendicular to
  // their 45° axis instead of running alongside each other.
  const tierOffsets = [46, 84, 122, 160, 198, 236, 274, 312];
  const TIER_X_STEP = -16;
  const tierLastX = tierOffsets.map(() => -Infinity);
  const TIER_MIN_GAP = 86;

  const msGroup = root.append('g');
  milestones.forEach((m) => {
    const mx = xTime(parseDay(m.releaseDate));
    if (mx < 0 || mx > inner.w) return;

    let tier = tierLastX.findIndex(lastX => mx - lastX > TIER_MIN_GAP);
    if (tier === -1) tier = tierLastX.indexOf(Math.min(...tierLastX));
    tierLastX[tier] = mx;
    const labelY = inner.h - tierOffsets[tier];
    const labelX = mx + 8 + tier * TIER_X_STEP;

    msGroup.append('line')
      .attr('x1', mx).attr('x2', mx)
      .attr('y1', 0).attr('y2', inner.h)
      .attr('stroke', m.color)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '2 4')
      .attr('opacity', 0.5);

    const g = msGroup.append('g')
      .attr('cursor', 'pointer')
      .on('click', () => selectScope(m.slug))
      .on('mousemove', (event) => {
        const [px, py] = pointer(event, container);
        tooltip
          .style('opacity', 1)
          .style('left', `${px + 16}px`)
          .style('top', `${py + 12}px`)
          .html(`
            <div class="tt-head">&gt; RELEASE :: ${m.label}</div>
            <div class="tt-row"><span class="tt-key">provider</span><span class="tt-val">${m.provider}</span></div>
            <div class="tt-row"><span class="tt-key">date</span><span class="tt-val">${m.releaseDate}</span></div>
            <div class="tt-row"><span class="tt-key">note</span><span class="tt-val">${m.highlight}</span></div>
          `);
      })
      .on('mouseleave', () => tooltip.style('opacity', 0));

    // Diamond marker on the baseline
    g.append('path')
      .attr('d', 'M0,-5 L5,0 L0,5 L-5,0 Z')
      .attr('transform', `translate(${mx},${inner.h})`)
      .attr('fill', m.color)
      .attr('stroke', TERM.bgInset)
      .attr('stroke-width', 1);

    // Connector from marker up to the label
    g.append('line')
      .attr('x1', mx).attr('x2', labelX)
      .attr('y1', inner.h).attr('y2', labelY + 4)
      .attr('stroke', m.color)
      .attr('stroke-width', 1)
      .attr('opacity', 0.8);

    const label = g.append('text')
      .attr('fill', TERM.textPrimary)
      .attr('font-size', 10)
      .attr('font-family', TERM.font)
      .attr('dominant-baseline', 'middle')
      .text(m.label.toUpperCase());

    const bbox = label.node().getBBox();

    // Rotated -45°, a label reaches (width + height)/√2 to the right of its
    // anchor. Late-window releases sit near the right edge, so pull the anchor
    // back in far enough to keep the box inside the plot; the connector is
    // already drawn as a diagonal, so it simply leans further.
    const rightReach = (bbox.width + bbox.height) / Math.SQRT2;
    const clampedX = Math.min(labelX, inner.w - rightReach);
    const placement = `translate(${clampedX},${labelY}) rotate(-45)`;

    label.attr('transform', placement);
    g.select('line:last-of-type').attr('x2', clampedX);

    // Terminal-style box behind the rotated label
    g.insert('rect', 'text')
      .attr('transform', placement)
      .attr('x', bbox.x - 5)
      .attr('y', bbox.y - 3)
      .attr('width', bbox.width + 10)
      .attr('height', bbox.height + 6)
      .attr('fill', '#000000')
      .attr('fill-opacity', 0.92)
      .attr('stroke', m.color)
      .attr('stroke-width', 1);
  });

  // ---- Crosshair + unified hover -----------------------------------------
  const crosshair = root.append('g').style('display', 'none');
  crosshair.append('line')
    .attr('y1', 0).attr('y2', inner.h)
    .attr('stroke', TERM.accentDim)
    .attr('stroke-width', 1)
    .attr('stroke-dasharray', '3 3');

  const markerGroup = root.append('g').style('display', 'none');

  const bisectDate = bisector(d => d).center;

  function indexFromPointer(mx) {
    if (isBand) {
      const step = xBand.step();
      const i = Math.floor((mx - (xBand.paddingOuter() * step)) / step);
      return Math.max(0, Math.min(spec.xValues.length - 1, i));
    }
    const date = xTime.invert(mx);
    return Math.max(0, Math.min(spec.xValues.length - 1, bisectDate(spec.xValues, date)));
  }

  function showHover(event) {
    const [mx, my] = pointer(event, root.node());
    if (mx < 0 || mx > inner.w || my < 0 || my > inner.h) return hideHover();

    const i = indexFromPointer(mx);
    const cx = xAt(i);

    crosshair.style('display', null).select('line').attr('x1', cx).attr('x2', cx);

    // Point markers for non-stacked series
    const markers = looseLayers.map(l => ({ color: l.color, y: yScale(Math.max(l.values[i], yScale.domain()[0])) }));
    markerGroup.style('display', null)
      .selectAll('circle')
      .data(markers)
      .join('circle')
      .attr('cx', cx)
      .attr('cy', d => d.y)
      .attr('r', 3.5)
      .attr('fill', TERM.bgInset)
      .attr('stroke', d => d.color)
      .attr('stroke-width', 2);

    // Tooltip rows — stacked series read top-down
    const rowLayers = stackedLayers.length ? [...looseLayers, ...stackedLayers].reverse() : [...looseLayers];
    const head = isBand ? spec.xValues[i] : fmtLongDay(spec.xValues[i]);

    const rows = rowLayers
      .map(l => {
        const v = l.values[i] || 0;
        if (l.stacked && v === 0) return '';
        const rawTxt = (l.raw && state.unit !== 'raw' && spec.yKind !== 'percent')
          ? `<span class="tt-raw">${fmtInt(l.raw[i] || 0)}</span>` : '';
        let display;
        if (isPercentStack) {
          const total = stackedLayers.reduce((s, sl) => s + (sl.values[i] || 0), 0);
          display = total > 0 ? ((v / total) * 100).toFixed(1) + '%' : '0.0%';
        } else {
          display = formatValue(v, spec);
        }
        return `
          <div class="tt-row">
            <span class="tt-swatch" style="background:${l.color}"></span>
            <span class="tt-key">${l.label}</span>
            <span class="tt-val">${display}${rawTxt}</span>
          </div>`;
      })
      .join('');

    const [px, py] = pointer(event, container);
    const flip = px > container.clientWidth - 300;
    tooltip
      .style('opacity', 1)
      .style('left', flip ? `${px - 290}px` : `${px + 18}px`)
      .style('top', `${Math.min(py + 10, height - 80)}px`)
      .html(`<div class="tt-head">&gt; ${head}</div>${rows}`);
  }

  function hideHover() {
    crosshair.style('display', 'none');
    markerGroup.style('display', 'none');
    tooltip.style('opacity', 0);
  }

  root.append('rect')
    .attr('width', inner.w)
    .attr('height', inner.h)
    .attr('fill', 'transparent')
    .style('cursor', 'crosshair')
    .on('mousemove', showHover)
    .on('mouseleave', hideHover);

  // ---- Legend (HTML, clickable) ------------------------------------------
  const legend = select(container).append('div').attr('class', 'chart-legend');
  legend.selectAll('button')
    .data(spec.layers)
    .join('button')
    .attr('class', d => 'legend-item' + (hiddenSeries.has(d.id) ? ' is-off' : ''))
    .attr('type', 'button')
    .html(d => `
      <span class="legend-swatch" style="background:${hiddenSeries.has(d.id) ? 'transparent' : d.color};border-color:${d.color}"></span>
      <span class="legend-label">${d.label}</span>
    `)
    .on('click', (_, d) => {
      if (hiddenSeries.has(d.id)) hiddenSeries.delete(d.id);
      else hiddenSeries.add(d.id);
      renderChart();
    });
}

/**
 * Renders the current SVG to a PNG download (replaces the old Plotly modebar).
 */
function downloadChartPNG() {
  const svgNode = document.querySelector('#chart-root svg');
  if (!svgNode) return;

  const clone = svgNode.cloneNode(true);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bg.setAttribute('width', '100%');
  bg.setAttribute('height', '100%');
  bg.setAttribute('fill', '#000000');
  clone.insertBefore(bg, clone.firstChild);

  const svgText = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const img = new Image();

  img.onload = () => {
    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = svgNode.clientWidth * scale;
    canvas.height = svgNode.clientHeight * scale;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);

    canvas.toBlob(pngBlob => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(pngBlob);
      link.download = `openrouter_${state.selectedScope.replace(/[:/]/g, '_')}_tokens.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    });
  };
  img.src = url;
}

/**
 * Export CSV
 */
function exportCSV() {
  const rows = [];
  const modelKeys = TRACKED_MODELS.map(m => m.slug);

  if (state.selectedScope.startsWith('group:')) {
    const grp = AGGREGATED_GROUPS[state.selectedScope];
    rows.push(['Date', `"${grp.shortLabel}_Total"`, ...grp.models.map(m => `"${MODEL_CONFIG[m]?.label || m}"`), 'Platform_Total', 'Group_Share_Pct'].join(','));
    state.rawDataset.forEach(d => {
      let gSum = 0;
      const subVals = grp.models.map(m => {
        const val = d.models[m] || 0;
        gSum += val;
        return val;
      });
      const sharePct = d.totalTokens > 0 ? ((gSum / d.totalTokens) * 100).toFixed(2) : '0.00';
      rows.push([d.date, gSum, ...subVals, d.totalTokens, sharePct].join(','));
    });
  } else if (state.selectedScope !== 'all') {
    const config = MODEL_CONFIG[state.selectedScope] || { label: state.selectedScope };
    rows.push(['Date', `${config.label}_Tokens`, 'Platform_Total_Tokens', 'Market_Share_Pct'].join(','));
    state.rawDataset.forEach(d => {
      const modelVal = d.models[state.selectedScope] || 0;
      const sharePct = d.totalTokens > 0 ? ((modelVal / d.totalTokens) * 100).toFixed(2) : '0.00';
      rows.push([d.date, modelVal, d.totalTokens, sharePct].join(','));
    });
  } else {
    rows.push(['Date', 'Platform_Total_Tokens', ...TRACKED_MODELS.map(m => `"${m.label}"`)].join(','));
    state.rawDataset.forEach(d => {
      const row = [
        d.date,
        d.totalTokens,
        ...modelKeys.map(k => d.models[k] || 0)
      ];
      rows.push(row.join(','));
    });
  }

  const csvContent = 'data:text/csv;charset=utf-8,' + rows.join('\n');
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `openrouter_${state.selectedScope}_token_usage.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Re-reads the weekly rankings straight from OpenRouter in the browser.
 * The endpoint is public and CORS-open, so this needs no API key — it just
 * pulls a fresher copy of the same series the build baked in.
 */
async function refreshLiveData() {
  const statusEl = document.getElementById('api-fetch-status');
  const setStatus = (cls, text) => {
    if (!statusEl) return;
    statusEl.className = 'api-feedback ' + cls;
    statusEl.textContent = text;
  };

  setStatus('loading', 'Fetching weekly rankings from OpenRouter...');

  try {
    const res = await fetch(CATALOG.sources.weekly, { headers: { accept: 'application/json' } });
    if (!res.ok) throw new Error(`rankings API returned ${res.status}`);

    const json = await res.json();
    const rows = json && json.data && json.data.data;
    if (!Array.isArray(rows) || !rows.length) throw new Error('no weekly rows in response');

    const todayISO = new Date().toISOString().slice(0, 10);
    const fresh = rows
      .filter(r => r.x >= CATALOG.seriesStart)
      .filter(r => Date.parse(r.x + 'T00:00:00Z') + 7 * 86400000 <= Date.parse(todayISO + 'T00:00:00Z'))
      .map(r => ({
        date: r.x,
        timestamp: Date.parse(r.x + 'T00:00:00Z'),
        totalTokens: Object.values(r.ys).reduce((a, b) => a + b, 0),
        models: { ...r.ys }
      }));

    if (!fresh.length) throw new Error('no complete weeks returned');

    state.rawDataset = fresh;
    state.isLive = true;

    const badge = document.getElementById('data-source-badge');
    if (badge) {
      badge.textContent = 'Live — rankings';
      badge.classList.add('live');
    }
    const statusText = document.getElementById('api-status-text');
    if (statusText) statusText.textContent = 'Live data';

    setStatus('success', `Loaded ${fresh.length} complete weeks through ${fresh[fresh.length - 1].date}.`);
    renderProvenance();
    renderChart();

    setTimeout(() => {
      const modal = document.getElementById('api-modal');
      if (modal) modal.classList.remove('open');
    }, 1000);
  } catch (err) {
    setStatus('error', `Live refresh failed: ${err.message}`);
  }
}

/**
 * Event Listeners & UI Binding
 */
function initUIEvents() {
  const modelFilter = document.getElementById('model-filter-select');
  if (modelFilter) {
    modelFilter.addEventListener('change', (e) => {
      selectScope(e.target.value);
    });
  }

  const viewControl = document.getElementById('view-mode-control');
  if (viewControl) {
    viewControl.addEventListener('click', (e) => {
      const btn = e.target.closest('.seg-btn');
      if (!btn) return;
      viewControl.querySelectorAll('.seg-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      state.currentView = btn.dataset.view;

      const maWrapper = document.getElementById('ma-toggle-wrapper');
      if (maWrapper) {
        maWrapper.style.display = (state.currentView === 'total' || state.currentView === 'market-share') ? 'flex' : 'none';
      }

      syncMeasuredBadge();

      renderChart();
    });
  }

  const rangeShortcuts = document.getElementById('range-shortcuts');
  if (rangeShortcuts) {
    rangeShortcuts.addEventListener('click', (e) => {
      const btn = e.target.closest('.range-btn');
      if (!btn) return;
      rangeShortcuts.querySelectorAll('.range-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      state.activeWeeks = btn.dataset.days;
      renderChart();
    });
  }

  const toggleMA = document.getElementById('toggle-ma');
  if (toggleMA) {
    toggleMA.addEventListener('change', (e) => {
      state.showMA = e.target.checked;
      renderChart();
    });
  }

  const toggleEvents = document.getElementById('toggle-events');
  if (toggleEvents) {
    toggleEvents.addEventListener('change', (e) => {
      state.showEvents = e.target.checked;
      renderChart();
    });
  }

  const scaleSelect = document.getElementById('scale-select');
  if (scaleSelect) {
    scaleSelect.addEventListener('change', (e) => {
      state.yScale = e.target.value;
      renderChart();
    });
  }

  const unitSelect = document.getElementById('unit-select');
  if (unitSelect) {
    unitSelect.addEventListener('change', (e) => {
      state.unit = e.target.value;
      renderChart();
    });
  }

  const exportBtn = document.getElementById('btn-export-csv');
  if (exportBtn) {
    exportBtn.addEventListener('click', exportCSV);
  }

  const pngBtn = document.getElementById('btn-download-png');
  if (pngBtn) {
    pngBtn.addEventListener('click', downloadChartPNG);
  }

  const modal = document.getElementById('api-modal');
  const openModalBtn = document.getElementById('btn-open-api-modal');
  const closeModalBtn = document.getElementById('btn-close-modal');

  if (openModalBtn && modal) {
    openModalBtn.addEventListener('click', () => modal.classList.add('open'));
  }
  if (closeModalBtn && modal) {
    closeModalBtn.addEventListener('click', () => modal.classList.remove('open'));
  }
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.remove('open');
    });
  }

  const fetchLiveBtn = document.getElementById('btn-fetch-live');
  if (fetchLiveBtn) {
    fetchLiveBtn.addEventListener('click', refreshLiveData);
  }

  const useSampleBtn = document.getElementById('btn-use-sample');
  if (useSampleBtn) {
    useSampleBtn.addEventListener('click', () => {
      state.rawDataset = loadMeasuredDataset();
      state.isLive = false;
      document.getElementById('data-source-badge').textContent = 'Measured — rankings';
      document.getElementById('data-source-badge').classList.remove('live');
      document.getElementById('api-status-text').textContent = 'Data Sources';
      if (modal) modal.classList.remove('open');
      renderChart();
    });
  }

  // Redraw on width changes only — the SVG height is fixed, so this cannot loop
  const chartRoot = document.getElementById('chart-root');
  if (chartRoot && typeof ResizeObserver !== 'undefined') {
    let lastWidth = chartRoot.clientWidth;
    const ro = new ResizeObserver(() => {
      const w = chartRoot.clientWidth;
      if (Math.abs(w - lastWidth) > 2) {
        lastWidth = w;
        renderChart();
      }
    });
    ro.observe(chartRoot);
  }
}

/**
 * Stamps the page with where its data came from, straight out of the catalog,
 * so the provenance moves with the build instead of being hardcoded copy.
 */
function renderProvenance() {
  const generated = new Date(CATALOG.generatedAt);
  const stampDate = isNaN(generated) ? CATALOG.asOf : generated.toISOString().slice(0, 10);

  const full = state.rawDataset;
  const latestWeek = full.length ? full[full.length - 1].date : CATALOG.asOf;

  const stamp = document.getElementById('catalog-stamp');
  if (stamp) {
    stamp.innerHTML = `<span class="pulse-dot"></span>Data ${stampDate}`;
    stamp.title = `Weekly rankings + ${CATALOG.catalogSize}-model catalog, read ${stampDate}`;
  }

  const footer = document.getElementById('footer-provenance');
  if (footer) {
    footer.innerHTML =
      `Token volumes: <code>${CATALOG.sources.weekly}</code> — ` +
      `${full.length} complete weeks through ${latestWeek}. ` +
      `Per-model detail: <code>${CATALOG.sources.leaderboard}</code>. ` +
      `Release dates, context windows and prices: <code>${CATALOG.sources.catalog}</code> ` +
      `(${CATALOG.catalogSize} models). Read ${stampDate}; refreshed on every deploy. ${CATALOG.usageNote}`;
  }
}

// Initialize
state.rawDataset = loadMeasuredDataset();
if (!DAILY_AVAILABLE) {
  // Built without an API key, so there is no daily series to show
  const dailyBtn = document.getElementById('seg-daily');
  if (dailyBtn) dailyBtn.style.display = 'none';
}
renderProvenance();
syncMeasuredBadge();
populateModelDropdown();
initUIEvents();
renderChart();
